import { useEffect } from 'react';

const FALLBACK_REVEAL_MS = 2500;

export default function useScrollReveal(deps = []) {
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      document.querySelectorAll('[data-reveal]').forEach((el) => el.classList.add('reveal-in'));
      return;
    }

    let io;
    let fallbackTimer;

    // rAF ensures elements are painted before we observe them
    const raf = requestAnimationFrame(() => {
      const els = document.querySelectorAll('[data-reveal]:not(.reveal-in)');
      if (!els.length) return;

      io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              // Siblings revealed together arrive one after the other; the
              // delay is dropped once done so it never slows a hover.
              const el = entry.target;
              const i = [...el.parentElement.children].filter((c) => c.hasAttribute('data-reveal')).indexOf(el);
              if (i > 0) {
                el.style.transitionDelay = `${Math.min(i * 0.08, 0.4)}s`;
                el.addEventListener('transitionend', () => { el.style.transitionDelay = ''; }, { once: true });
              }
              entry.target.classList.add('reveal-in');
              io.unobserve(entry.target);
            }
          });
        },
        { threshold: 0, rootMargin: '0px' }
      );

      els.forEach((el) => io.observe(el));

      // Safety net: if the observer never fires (unsupported browser quirk,
      // element never enters viewport, etc.) content must not stay hidden forever.
      fallbackTimer = setTimeout(() => {
        document.querySelectorAll('[data-reveal]:not(.reveal-in)').forEach((el) => el.classList.add('reveal-in'));
      }, FALLBACK_REVEAL_MS);
    });

    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(fallbackTimer);
      if (io) io.disconnect();
    };
  }, deps); // eslint-disable-line
}
